import React from 'react';
import { MapPin } from 'lucide-react';
import { TRADE_COUNTRIES } from '../constants/tradeCountries';

const inputClass =
  'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none';

// Pin the most common markets to the top of the country dropdown.
const PINNED_COUNTRY_CODES = ['GB', 'IE'];
const PINNED_COUNTRIES = PINNED_COUNTRY_CODES
  .map((code) => TRADE_COUNTRIES.find((c) => c.code === code))
  .filter(Boolean);
const OTHER_COUNTRIES = TRADE_COUNTRIES.filter((c) => !PINNED_COUNTRY_CODES.includes(c.code));
const TRADE_COUNTRY_NAMES = new Set(TRADE_COUNTRIES.map((c) => c.name));

/**
 * Structured address with HTML5 autocomplete for browser / password-manager suggestions.
 * @param {{ addressLine1, addressLine2, addressCity, addressRegion, addressPostcode, addressCountry }} value
 * @param {(patch: object) => void} onChange — merge partial updates into parent state
 * @param {boolean} [required] — mark required fields with * (visual only; parent validates)
 */
export default function AddressFieldsSection({ value, onChange, required = true }) {
  const v = value || {};
  const set = (key) => (e) => onChange({ [key]: e.target.value });

  const req = required ? ' *' : '';

  return (
    <fieldset className="space-y-3 border-0 p-0 m-0">
      <legend className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
        <MapPin size={12} /> Address{required ? ' *' : ''}
      </legend>
      <p className="text-[11px] text-slate-400 -mt-1 mb-2">
        Use separate fields so we can reach you reliably. Your browser may suggest an address as you type.
      </p>

      <div>
        <label className="block text-[11px] font-medium text-slate-600 mb-1">Street address{req}</label>
        <input
          type="text"
          name="address-line1"
          value={v.addressLine1 || ''}
          onChange={set('addressLine1')}
          className={inputClass}
          autoComplete="address-line1"
          placeholder="House name or number, street"
        />
      </div>
      <div>
        <label className="block text-[11px] font-medium text-slate-600 mb-1">Address line 2 (optional)</label>
        <input
          type="text"
          name="address-line2"
          value={v.addressLine2 || ''}
          onChange={set('addressLine2')}
          className={inputClass}
          autoComplete="address-line2"
          placeholder="Apartment, suite, unit, etc."
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-medium text-slate-600 mb-1">City / town{req}</label>
          <input
            type="text"
            name="city"
            value={v.addressCity || ''}
            onChange={set('addressCity')}
            className={inputClass}
            autoComplete="address-level2"
            placeholder="City or town"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-600 mb-1">County / state / region</label>
          <input
            type="text"
            name="region"
            value={v.addressRegion || ''}
            onChange={set('addressRegion')}
            className={inputClass}
            autoComplete="address-level1"
            placeholder="Optional"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-medium text-slate-600 mb-1">Postcode / ZIP{req}</label>
          <input
            type="text"
            name="postal-code"
            value={v.addressPostcode || ''}
            onChange={set('addressPostcode')}
            className={inputClass}
            autoComplete="postal-code"
            placeholder="Postcode or ZIP"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-600 mb-1">Country{req}</label>
          <select
            name="country"
            value={v.addressCountry || ''}
            onChange={set('addressCountry')}
            className={`${inputClass} bg-white`}
            autoComplete="country-name"
          >
            <option value="">Select a country…</option>
            {/* Preserve a legacy / free-text value that isn't in the trade list
                so existing profiles aren't silently wiped on next save. */}
            {v.addressCountry && !TRADE_COUNTRY_NAMES.has(v.addressCountry) && (
              <option value={v.addressCountry}>{v.addressCountry}</option>
            )}
            {PINNED_COUNTRIES.length > 0 && (
              <optgroup label="Frequently selected">
                {PINNED_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </optgroup>
            )}
            <optgroup label="All countries">
              {OTHER_COUNTRIES.map((c) => (
                <option key={c.code} value={c.name}>{c.name}</option>
              ))}
            </optgroup>
          </select>
        </div>
      </div>
    </fieldset>
  );
}
